import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import classes from './EndedAuctionBanner.module.scss';
import { getUserPurchases } from '../../services';
import { getMyDealerPurchases } from '../../services/dealerGarage';

const JST_DATE = /^\d{4}\/\d{2}\/\d{2}$/;

/**
 * Whether this car's auction is over: it has been archived, or its date is
 * before today in Japan. bidDetail.bidDate is a JST 'YYYY/MM/DD' string, the
 * same one the auction list and the API's bid check (createBid) compare.
 */
export const isAuctionOver = (car) => {
    if (!car) return false;
    if (car.isArchived === true || car.source === 'oldcars') return true;
    const date = car.bidDetail && car.bidDetail.bidDate;
    return typeof date === 'string' && JST_DATE.test(date)
        && date < DateTime.now().setZone('Asia/Tokyo').toFormat('yyyy/LL/dd');
};

const auctionDay = (car) => {
    const date = car && car.bidDetail && car.bidDetail.bidDate;
    if (typeof date !== 'string' || !JST_DATE.test(date)) return '';
    return DateTime.fromFormat(date, 'yyyy/LL/dd').toFormat('LLLL d, yyyy');
};

const sameId = (value, id) => String((value && (value._id || value.$oid)) || value || '') === id;

/**
 * The member's own purchase of this car, or null: a pipeline record (an
 * auction win staff move through the shipping steps) or an older auction
 * purchase. Never throws - the banner just says the auction ended.
 */
const findPurchase = async (carId, userId) => {
    const [pipeline, older] = await Promise.all([
        getMyDealerPurchases().catch(() => null),
        getUserPurchases(userId).catch(() => null),
    ]);
    const won = ((pipeline && pipeline.data) || [])
        .find((p) => p.source === 'auction' && p.vehicleId === carId && p.status !== 'Cancelled');
    if (won) {
        return {
            status: won.status,
            summary: won.stepInfo && won.stepInfo.summary,
            href: `/garage?tab=purchases&purchase=${won._id}`,
        };
    }
    const bought = ((older && older.data) || []).find((p) => sameId(p.carId, carId));
    return bought ? { status: bought.status, href: '/garage?source=auction&tab=purchases' } : null;
};

/**
 * Shown above a car whose auction is over. The page is then a record of the
 * car rather than a listing (bidding is hidden), and the member who bought it
 * is told so, with where it is now and a way back to it in My Garage.
 */
const EndedAuctionBanner = ({ car, userId }) => {
    const [purchase, setPurchase] = useState(null);
    const ended = isAuctionOver(car);
    const carId = car && car._id ? String(car._id) : '';

    useEffect(() => {
        setPurchase(null);
        if (!ended || !carId || !userId) return undefined;
        let current = true;
        findPurchase(carId, userId).then((found) => { if (current) setPurchase(found); });
        return () => { current = false; };
    }, [ended, carId, userId]);

    if (!ended) return null;
    const day = auctionDay(car);

    if (purchase) {
        return (
            <div className={`${classes.banner} ${classes.purchased}`} role="status">
                <i className={`fas fa-key ${classes.icon}`} aria-hidden="true"></i>
                <div className={classes.body}>
                    <div className={classes.titleRow}>
                        <strong className={classes.title}>You bought this car</strong>
                        {purchase.status && <span className={classes.step}>{purchase.status}</span>}
                    </div>
                    <p className={classes.text}>
                        {day ? `Its auction ended on ${day}.` : 'Its auction has ended.'}
                        {purchase.summary ? ` ${purchase.summary}` : ''}
                    </p>
                </div>
                <Link to={purchase.href} className={classes.action}>
                    Track it in My Garage
                </Link>
            </div>
        );
    }

    return (
        <div className={classes.banner} role="status">
            <i className={`fas fa-flag-checkered ${classes.icon}`} aria-hidden="true"></i>
            <div className={classes.body}>
                <strong className={classes.title}>This auction has ended</strong>
                <p className={classes.text}>
                    {day ? `Bidding closed on ${day}.` : 'Bidding is closed.'} This page is kept as a record of the car.
                </p>
            </div>
            <Link to="/auction" className={classes.action}>
                See current auctions
            </Link>
        </div>
    );
};

export default EndedAuctionBanner;
